import {ImageMaterial} from './rendering';
import {
  PointCloudManager,
  GenericPointCloud
} from './pointcloud';
import {CompassControls, PanoControls} from './controls';
import { mergeBoundingBoxes, toScreenPosition } from './utilities';
import {CameraImage, CameraList, CameraLoadOptions} from './cameras';
import {CameraMarkers} from './markers';
import {
  AerialMap,
  extentFromPoints
} from './aerial';
import {Clamp, ColorMapKey, ColorType, MarkerNavType} from '../../redux/settings-slice';
import LocalScene, {
  DataProjectionCoordinate,
  ProjectedCoordinate,
  SceneCoordinate,
  webMercator
} from './projections';
import { getColorMap } from "../textures/colormaps";
import {
  MeasurementController,
  Measurement
} from './measurements';
import { ChangeDetector } from './utilities';
import { isDevMode } from '../../electron-modules';
import GPUPicker from './gpu-picker';
import { throttle } from 'throttle-debounce';
import { SceneCameraState } from '../../redux/camera-slice';
import { ImageLabelTools, OrthoCategory } from './labelling';
import {
  Vector3,
  PerspectiveCamera,
  WebGLRenderer,
  TextureLoader,
  Line3
} from "three";
import Stats from '../../stats.module.js';
import { RenderUtils, BasicRenderTarget } from './rendering';
import { MultiImageWindow } from './controls';
import onresize from "resize-event";
import Tags, { TagItem } from './tags';
import {toast} from '../../app';
import { updateMouseCoords } from '../../app-bar';
import { CADHandler } from './linework';
import { t } from '../../localization';
import { setCameraState } from '../viewer';
import path from 'path';
import { Bookmark } from '../../redux/bookmarks-slice';
import { VolumeType, SamplingRate, MeasureType } from '../../types/measurements';
import { WebSourceList } from './web-sources';

const annotationStyle = {
  transform: "translate(-50%, -50%)",
  background:  "rgba(0,0,0,0.6)",
  cursor:  "pointer",
  borderRadius: "4px",
  color:  "white",
  fontSize: "0.7em",
  padding:  "4px 8px",
  position:  "absolute",
  whiteSpace: "nowrap",
  userSelect: "none"
};

export class Viewer {
  public readonly containerElement: HTMLElement;
  public defaultFarPlane: number = 10000;
  private defaultNearPlane: number = 0.1;
  public pixelRatio = 1.0;
  public camera: PerspectiveCamera;
  public renderer: WebGLRenderer;
  private imageRenderMaterial: ImageMaterial;
  private imageRenderTarget: BasicRenderTarget;
  public gpuPickers: {
    default: GPUPicker,
    markers: GPUPicker,
    tags: GPUPicker
  };
  public imageLabels: ImageLabelTools;
  private renderUtils: RenderUtils;
  private changeDetector: ChangeDetector;
  public compass: CompassControls;
  public controls: PanoControls;
  public pointclouds: PointCloudManager;
  public cameraList: CameraList;
  public minimap: AerialMap;
  public markers: CameraMarkers;
  public measurements: MeasurementController;
  public cadHandler: CADHandler;
  public webmapHandler: WebSourceList;
  public tags: Tags;
  public multiImageWindow: MultiImageWindow;
  private stats: StatsPanel;
  public mouseCoords: Vector3;
  private updateMouseCoords: Function;
  private annotations = [];
  private enabled = true;
  public isEmpty = true;
  private focused = false;
  private drawPickerScene = false;
  public expanded = false;

  public projectName;
  private observer;

  // TODO: add intial aerial state, and make these... nicer?
  private initialZoomApplied = false;
  private initialStateApplied = false;

  public timingDebug = {
    "markers": false,
    "scene": false
  };

  constructor(viewContainer, mapContainer, props) {
    this.containerElement = viewContainer;

    this.initRenderer();
    this.initGpuPickers();
    this.initMaterials();
    this.initEvents();
    this.updateCamera();

    this.updateMouseCoords = throttle(100, (event) => {
      let coords = event
        ? this.controls.getPickerResults(event).coordinate
        : null;

      updateMouseCoords(coords);
    });

    this.compass = new CompassControls(this, props);
    this.pointclouds = new PointCloudManager(this);
    this.controls = new PanoControls(this, this.camera,
      this.renderer.domElement, props);
    this.measurements = new MeasurementController(this, props);
    this.cameraList = new CameraList(this);
    this.minimap = new AerialMap(this, mapContainer, props);
    this.tags = new Tags(this);
    this.cadHandler = new CADHandler(this);
    this.webmapHandler = new WebSourceList(this);
    this.imageLabels = new ImageLabelTools(this, props);
    this.markers = new CameraMarkers(this);
    this.changeDetector = new ChangeDetector(this);
    this.multiImageWindow = new MultiImageWindow(this, props);
    this.stats = new StatsPanel(this);

    this.addContainerElements();
    this.initResize();
    this.animate();
  }

  get width(): number {
    return this.containerElement.offsetWidth;
  }

  get height(): number {
    return this.containerElement.offsetHeight;
  }

  get near() {
    return this.camera.near;
  }

  get far() {
    return this.camera.far;
  }

  get fov() {
    return this.camera.fov;
  }

  get orthoList() {
    return this.minimap.orthoList;
  }

  get lookatVector() {
    return new Vector3(0, 0, -1)
      .applyQuaternion(this.camera.quaternion);
  }

  get upVector() {
    return new Vector3(0, 1, 0)
      .applyQuaternion(this.camera.quaternion);
  }

  get rightVector() {
    return new Vector3(1, 0, 0)
      .applyQuaternion(this.camera.quaternion);
  }

  get orbitState(): boolean {
    return this.controls.orbit.state;
  }

  get cameraSelected() {
    return this.cameraList.cameraLoaded;
  }

  get hasPoints() {
    return !this.pointclouds.isEmpty;
  }

  get hasCameras() {
    return !this.cameraList.isEmpty;
  }

  get hasModels() {
    return this.cadHandler.numberOfFiles > 0;
  }

  get hasTags() {
    return this.tags.numberOfFiles > 0;
  }

  get isImageOnly() {
    return this.hasCameras && !this.hasPoints;
  }

  get isPointsOnly() {
    return !this.hasCameras && this.hasPoints;
  }

  get calculatingSceneMarkers() {
    return this.markers.calculating;
  }

  get calculatingAerialMarkers() {
    return this.minimap.calculating;
  }

  get showCompassElement() {
    if (!LocalScene.initialized) return false;
    let show = this.orbitState;

    if (this.hasCameras) {
      // Current camera is done loading (or there is no camera selected)
      let currentCamera = this.cameraList.current;
      show = currentCamera
        ? currentCamera.loaded || currentCamera.loading
        : true;
    }

    return show;
  }

  get initialCameraLoading() {
    return this.cameraList.newCameraLoading
      && !this.cameraList.cameraLoadedOnce;
  }

  get aerialState() {
    return this.minimap.aerialState;
  }

  get sceneState() {
    let state = {
      camera: null,
      fov: null,
      lookat: null,
      angles: null,
      orbit: null,
      pivot: null
    } as SceneCameraState;

    if (this.orbitState) {
      // Update for orbit camera
      let {orbit, pivot} = this.controls.orbit.getPositionValues();

      state = {
        ...state,
        orbit: orbit,
        pivot: pivot
      };
    } else if (this.cameraList.current) {
      // Update for standard camera
      state = {
        ...state,
        camera: this.cameraList.current.id,
        fov: this.controls.fov,
        angles: this.controls.angles
      };
    }

    return state;
  }

  /** Exposed Global Methods */
  setExpanded(state) {
    this.expanded = state;
  }

  setContainerCursor(cursor) {
    this.containerElement.style.cursor = cursor;
  }

  addContainerElements() {
    this.containerElement.appendChild(this.renderer.domElement);
    this.containerElement.appendChild(this.compass.domElement);
  }

  removeContainerElements() {
    this.containerElement.removeChild(this.renderer.domElement);
    this.containerElement.removeChild(this.compass.domElement);
  }

  addAnnotation(text: string, position: number[], onClick = null, customStyle = null) {
    const div = document.createElement("div");

    Object.keys(annotationStyle).forEach(key => {
      div.style[key] = annotationStyle[key];
    });

    if (customStyle) {
      Object.keys(customStyle).forEach(key => {
        div.style[key] = customStyle[key];
      });
    }

    div.innerHTML = text;

    if (onClick) {
      div.onclick = onClick;
    } else {
      div.style.pointerEvents = "none";
    }

    const annotation = {
      position: position,
      element: div
    };

    this.annotations.push(annotation);
    this.containerElement.appendChild(annotation.element);

    return annotation;
  }

  removeAnnotation(annotation) {
    const index = this.annotations.indexOf(annotation);
    if (index === -1) return;

    this.annotations.splice(index, 1);
    this.containerElement.removeChild(annotation.element);
  }

  setProjectName(name: string) {
    this.projectName = name;
  }

  togglePickerDebug(value) {
    this.drawPickerScene = value;
  }

  getAvailableClassifications(): number[] {
    return this.pointclouds.uniqueClassifications;
  }

  setVisibleClassifications(classifications) {
    this.pointclouds.setVisibleClassifications(classifications);
  }

  setPointsShouldUpdate(state) {
    this.pointclouds.shouldUpdate = state;
  }

  setPointBoundsVisible(state) {
    this.pointclouds.boundsVisible = state;
  }

  setPointCloudOpacity(opacity: number) {
    this.pointclouds.transparency = opacity;
  }

  setPointCloudMinimum(size: number) {
    this.pointclouds.setPointCloudMinimum(size);
  }

  setPointCloudScale(size: number) {
    this.pointclouds.setPointCloudScale(size);
  }

  setPointCloudMaxPoints(points: number) {
    this.pointclouds.visiblePointsTarget = points * 1000000;
  }

  setPointCloudDistance(distance: number) {
    this.pointclouds.setMaxDistance(distance);
  }

  setPointCloudHeightClip(clamp: Clamp) {
    this.pointclouds.updateClamping('heightClamp', clamp);
  }

  setPointCloudIntensityClip(clamp: Clamp) {
    this.pointclouds.updateClamping('intensityClamp', clamp);
  }

  setPointCloudColorMap(colorMapKey: ColorMapKey) {
    const textureData = getColorMap(colorMapKey).image;

    const loader = new TextureLoader();
    loader.load(textureData, texture => {
      this.pointclouds.materialDefaults.colorMap = colorMapKey;
      this.pointclouds.clouds.forEach(pointCloud => {
        pointCloud.materials.forEach(material => {
          material.colorMap = texture;
          material.colorMap.needsUpdate = true;
        });
      });
    });
  }

  setPointCloudColorType(colorType: ColorType) {
    this.pointclouds.materialDefaults.colorType = colorType;
    this.pointclouds.clouds.forEach(pointCloud => {
      pointCloud.materials.forEach(material => {
        material.colorType = colorType;
      });
    });
  }

  setPointCloudDynamicSize(state: boolean) {
    this.pointclouds.materialDefaults.dynamicSize = state;
    this.pointclouds.clouds.forEach(pointCloud => {
      pointCloud.materials.forEach(material => {
        material.adaptiveSize = state;
      });
    });
  }

  setPointCloudCircularPoints(state: boolean) {
    this.pointclouds.materialDefaults.circularPoints = state;
    this.pointclouds.clouds.forEach(pointCloud => {
      pointCloud.materials.forEach(material => {
        material.circularPoints = state;
      });
    });
  }

  setTagDistance(distance: number) {
    this.tags.setMaxDistance(distance);
  }

  setNavigationType(state: MarkerNavType) {
    this.markers.setNavigationType(state);
  }

  setControlType(type: number) {
    this.controls.setControlType(type);
  }

  toggleOrbitState() {
    this.controls.toggleCamera();
  }

  resetCamera() {
    this.controls.resetCamera("top");
  }

  /** NOTE: Temporary(?) solution for the viewer being created over and over again */
  destroy() {
    this.observer.disconnect();

    // Destroy viewer + events
    this.enabled = false;
    this.camera = null;
    this.removeContainerElements();
    this.removeEvents();

    this.minimap.destroy();
    this.controls.destroy();
    this.orthoList.destroy();
    this.multiImageWindow.destroy();
  }

  setDataProjection(newProjection) {
    LocalScene.setDataProjection(newProjection);
  }

  setViewProjection(newProjection) {
    LocalScene.setViewProjection(newProjection);
  }

  setMeasurementUnits(units) {
    this.controls.measurements.setUnits(units);
  }

  setActiveAlignment(id) {
    this.measurements.setActiveAlignment(id);
  }

  async loadImage(cameraID, opts: CameraLoadOptions = {}) {
    this.setInitialZoomFlag(true);
    let newCamera = await this.cameraList.loadImage(cameraID, opts);
    return newCamera;
  }

  /** @private */
  setCameraState(state: SceneCameraState) {
    if (this.initialStateApplied) return false;

    if (state.camera || state.lookat) {
      if (this.cameraList.loadSavedCameraState(state)) {
        this.setInitialStateFlag(true);
        return true;
      }
    }

    return false;
  }

  /** @private */
  setOrbitState(state: SceneCameraState) {
    if (this.initialStateApplied) return false;

    const has3dAssets = this.hasModels || this.hasTags || this.hasPoints;
    if (!has3dAssets) return false;

    if (state.orbit && state.pivot) {
      if (!this.orbitState) {
        // Move to orbit mode with default lookat
        this.controls.setOrbitValues(true);
      }

      const newPosition = {
        orbit: new DataProjectionCoordinate(state.orbit).toScene(),
        pivot: new DataProjectionCoordinate(state.pivot).toScene(),
        smooth: false
      };

      // Apply new positions to orbit camera
      this.controls.setOrbitValues(true, newPosition);
      this.setInitialZoomFlag(true);
      this.setInitialStateFlag(true);

      return true;
    }

    return false;
  }

  /** @private */
  setTagState(tagID: string) {
    if (this.initialStateApplied) return false;

    this.jumpToTag(tagID);
    this.setInitialStateFlag(true);
    return true;
  }

  clearCadHighlight() {
    this.cadHandler.clearHighlight();
  }

  getAerialZoomLevel() {
    return this.minimap?.map.getView().getZoom();
  }

  getCameraPosition() {
    return new SceneCoordinate(this.camera.position)
      .toDataProjection();
  }


  isBehindCamera(position: Vector3) {
    const start = new Vector3()
      .add(this.camera.position);

    const end = new Vector3()
      .add(this.camera.position)
      .add(this.lookatVector);

    const line = new Line3(start, end);
    return line.closestPointToPointParameter(position) < 0;
  }

  /** Local Aligner */
  toggleLocalAligner(state) {
    this.controls.localAligner.setState(state);
  }

  estimateLocalTransform = (estimator: string, units: string) => {
    return this.controls.localAligner.getLocalProjection(estimator, units);
  };

  clearLocalTransformErrors = () => {
    this.controls.localAligner.resetErrorValues();
  };

  refreshLocalTransform() {
    this.controls.localAligner.drawObservations();
  }

  generateLocalAlignerRows(numRows) {
    this.controls.localAligner.addNewObservations(numRows);
  }

  setLocalAlignerActive(id, type) {
    this.controls.localAligner.setActiveObservation(id, type);
  };

  deleteLocalAlignment(id) {
    this.controls.localAligner.removeByID(id);
  }

  /** Point Markup */
  pointMarkupActive() {
    return this.controls.pointMarkup.active;
  }

  addPointMarkupOperation(type, name) {
    this.controls.pointMarkup.addNewGroup(type, name);
  }

  updatePointMarkupName(id, name) {
    this.controls.pointMarkup.setName(id, name);
  }

  setPointMarkupActive(id) {
    this.controls.pointMarkup.setActive(id);
  }

  pointMarkupToggle(state) {
    this.controls.pointMarkup.setState(state);
  }

  clearPointMarkup() {
    this.controls.pointMarkup.clear();
  }

  pointMarkupZoom(id) {
    this.controls.pointMarkup.zoom(id);
  }

  pointMarkupDeleteGroup(id) {
    this.controls.pointMarkup.removeClippingGroup(id);
  }

  pointMarkupDeleteBox(id) {
    this.controls.pointMarkup.removeClippingBox(id);
  }

  pointMarkupNameExists(name) {
    return this.controls.pointMarkup.clippingGroups
      .filter(x => x.name === name).length > 0;
  }

  setPointMarkupNewClassification(id, value) {
    this.controls.pointMarkup.setNewClassification(id, value);
  }

  setPointMarkupIgnoredClassification(id, value) {
    this.controls.pointMarkup.setIgnoredClassification(id, value);
  }

  resetPointMarkupRotation(id) {
    this.controls.pointMarkup.resetRotation(id);
  }

  setPointMarkupHover(id, hover) {
    this.controls.pointMarkup.setHover(id, hover);
  }

  /** Multi Image Window */
  initMultiImageContainer(container) {
    this.multiImageWindow.init(container);
  }

  toggleMultiImageWindow(state) {
    this.multiImageWindow.setState(state);
  }

  multiImageNextImage() {
    this.multiImageWindow.loadNextImage();
  }

  multiImagePreviousImage() {
    this.multiImageWindow.loadPreviousImage();
  }

  multiImageOnComplete() {
    this.multiImageWindow.onComplete();
  }

  multiImageOnAddMore() {
    this.multiImageWindow.onAddMore();
  }

  multiImageClosePrompt() {
    return this.multiImageWindow.closePrompt();
  }

  /** Point Profile */
  initProfileContainer(type, units) {
    this.controls.pointProfile.profileChart.init(type, units);
  }

  resetPointProfile() {
    this.controls.pointProfile.reset();
  }

  refreshPointProfile() {
    this.controls.pointProfile.updatePoints();
  }

  resetPointProfileView() {
    this.controls.pointProfile.resetZoom();
  }

  pointProfileActive() {
    return this.controls.pointProfile.drawing;
  }

  pointProfileStart(coordinate) {
    this.controls.pointProfile.selectStart(coordinate);
  }

  pointProfileEnd() {
    this.controls.pointProfile.selectEnd();
  }

  pointProfileCancel() {
    this.controls.pointProfile.selectCancel();
  }

  setPointProfileUnits(units) {
    this.controls.pointProfile.updateUnits(units);
  }

  setPointProfileSampling(type) {
    this.controls.pointProfile.updateSampling(type);
  }

  getPointProfileImage() {
    return this.controls.pointProfile.getProfileImage();
  }

  /** Measurements */
  toggleMeasurements(state) {
    this.controls.measurements.setState(state);
  }

  updateVolumeType(type: VolumeType) {
    this.controls.measurements.setVolumeType(type);
  }

  updateVolumeSampleRate(rate: SamplingRate) {
    this.controls.measurements.setSampleRate(rate);
  }

  updateMeasurementType(type: MeasureType) {
    this.controls.measurements.setMeasurementType(type);
  }

  updateMeasureHover(measurement: Measurement, state) {
    this.controls.measurements.setHover(measurement, state);
  }

  deleteMeasurement(measurement: Measurement) {
    this.controls.measurements.removeByID(measurement.id);
  }

  renameMeasurement(measurement: Measurement, title) {
    this.controls.measurements.setTitle(measurement.id, title);
  }

  exportMeasurements(type, ordering) {
    this.controls.measurements.export(type, ordering);
  }

  getMeasurementFormatter() {
    return this.controls.measurements.formatter;
  }

  loadMeasurementData(data) {
    return this.controls.measurements.loadMeasurementData(data);
  }

  /** Ortho Viewer */
  setOrthoOpacity(value) {
    this.orthoList.setOpacity(value);
  }

  /** Labelling/Annotation */
  activeImageLabelPaths() {
    return this.imageLabels.activeImagePaths();
  }

  activeImages() {
    return this.imageLabels.activeImages();
  }

  loadedImageLabelPaths() {
    return this.imageLabels.loadedImagePaths();
  }

  async loadImageLabelData(data, progressCallback, overwrite, checkOverlap) {
    await this.imageLabels.loadImageLabelData(
      data, progressCallback, overwrite, checkOverlap);
  }

  setActiveLabelType(state) {
    this.imageLabels.setActiveLabelType(state);
  }

  labelSelected() {
    return this.imageLabels.checkIfSelected();
  }

  labelHotkey(event) {
    this.imageLabels.onKeyDown(event);
  }

  setLabelText(id, text) {
    this.imageLabels.setText(id, text);
  }

  setLabelFindText(id) {
    this.imageLabels.setFindText(id);
  }

  addCategory(category) {
    this.imageLabels.addCategory(category);
  }

  deleteCategory(id) {
    this.imageLabels.deleteCategory(id);
  }

  addTrainingArea() {
    this.imageLabels.addTrainingArea();
  }

  deleteHighlighted() {
    this.imageLabels.deleteHighlighted();
  }

  editCategoryName(id, name) {
    this.imageLabels.editCategoryName(id, name);
  }

  deleteAllCategories() {
    this.imageLabels.deleteAllCategories();
  }

  deleteTrainingArea() {
    this.imageLabels.deleteTrainingArea();
  }

  deleteLabel(id) {
    this.imageLabels.deleteLabel(id);
  }

  zoomToLabel(id) {
    this.imageLabels.zoomToLabel(id);
  }

  addLabel(id) {
    this.imageLabels.addLabel(id);
  }

  addTrainingAreaLabel() {
    this.imageLabels.addTrainingAreaLabel();
  }

  currentCategory() {
    return this.imageLabels.currentCategoryName;
  }

  getIdxInList(category) {
    return this.imageLabels.getIdxInList(category);
  }

  selectLabel(id) {
    this.imageLabels.selectLabel(id);
  }

  deleteLabelPoint(pointID) {
    this.imageLabels.deletePoint(pointID);
  }

  insertLabelPoint(id, coord) {
    this.imageLabels.insertPoint(id, coord);
  }

  editLabel(id, coord) {
    this.imageLabels.editLabel(id, coord);
  }

  moveToLabel(id) {
    this.imageLabels.moveToLabelInList(id);
  }

  completeLabel(label) {
    this.imageLabels.completeLabel(label);
  }

  unHighlight(id) {
    this.imageLabels.unHighlight(id);
  }

  unHighlightAll() {
    this.imageLabels.unHighlightAll();
  }

  sortCategory = (category, score, sortType) => {
    this.imageLabels.sortCategory(category, score, sortType);
  };

  undoSort = (category) => {
    this.imageLabels.undoSort(category);
  };

  numLabelsHighlighted() {
    return this.imageLabels.numHighlighted;
  }

  checkCategoryUniqueness(categoryName) {
    return this.imageLabels.checkUniqueName(categoryName);
  }

  labelObject = () => {
    return this.imageLabels.labelObject();
  };

  getOrthoLabelPolygons = () => {
    if (!this.imageLabels.isAerialLabels) return;

    const data = {};

    this.imageLabels.categories.forEach((category: OrthoCategory) => {
      const segments = category.labels
        .filter(label => label.valid)
        .map(label => {
          return label.polygonPointsOnly.map(coord => {
            return new ProjectedCoordinate(coord, webMercator)
              .toDataProjection()
              .toArray()
              .slice(0, 2);
          });
        });

      data[category.name] = segments;
    });

    return data;
  };

  anyLabelSelected = () => {
    return this.imageLabels.checkIfSelected();
  };

  getLasFileInfo(id) {
    const pointCloud = this.pointclouds.getByID(id);
    if (!pointCloud) return;
    if (!(pointCloud instanceof GenericPointCloud)) return;

    return pointCloud.headerFileInfo;
  }

  /** Asset Click */

  snapToFolder(assetIDs) {
    let boundingBoxes = [];

    assetIDs.forEach(assetID => {
      const file = this.cadHandler.getByID(assetID);
      if (file) {
        boundingBoxes.push(file.boundingBox);
      }

      const pointCloud = this.pointclouds.getByID(assetID);
      if (pointCloud) {
        boundingBoxes.push(pointCloud.tightBoundingBox);
      }

      const tagFile = this.tags.getByID(assetID);
      if (tagFile) {
        const tagBBOX = this.tags.boundingBoxForTags(tagFile.tags);
        boundingBoxes.push(tagBBOX);
      }
    });

    const combinedBounds = mergeBoundingBoxes(boundingBoxes);

    this.setInitialZoomFlag(false);
    this.zoomToSceneExtent(combinedBounds, true);
  }

  snapToOrtho(id) {
    const ortho = this.minimap.orthoList.getByID(id);
    if (!ortho) return;

    ortho.zoomToExtent();
  }

  snapToModel(id) {
    const file = this.cadHandler.getByID(id);
    if (!file) return;

    this.setInitialZoomFlag(false);
    this.zoomToSceneExtent(file.boundingBox, true);
  }

  snapToPointCloud(id) {
    const pointCloud = this.pointclouds.getByID(id);
    if (!pointCloud) return;

    this.setInitialZoomFlag(false);
    this.zoomToSceneExtent(pointCloud.tightBoundingBox, true);
  }

  snapToImages(assetID) {
    const features = this.getCameraFeaturesForCSV(assetID);
    if (features.length === 0) return;

    let positions = features.map(x => x.getGeometry().getCoordinates());
    let extent = extentFromPoints(positions, false);
    this.minimap.setMapExtent(extent);
  }

  zoomToSceneExtent(boundingBox, smooth: boolean) {
    if (!boundingBox || this.initialZoomApplied) return;

    try {
      this.setInitialZoomFlag(true);

      let newPosition = this.controls.orbitFromBounds(
        boundingBox, "top", smooth);

      // Apply new positions to orbit camera
      this.controls.setOrbitValues(true, newPosition);

      // Zoom to map extent
      let positions = [boundingBox.min, boundingBox.max].map(position => {
        return new SceneCoordinate(position).toDataProjection();
      });

      let extent = extentFromPoints(positions);
      this.minimap.setMapExtent(extent);
    } catch (err) {
      console.error("Error zooming to extent", boundingBox);
    }
  }

  /** Camera Aligner */
  toggleCameraAligner(state) {
    this.controls.cameraAlignerAdv.setState(state);
  }

  resetCameraAligner() {
    this.controls.cameraAlignerAdv.reset();
  }

  generateAdvancedAlignerRows(numRows) {
    this.controls.cameraAlignerAdv.addNewObservations(numRows);
  }

  setErrorsCameraAligner(errors) {
    this.controls.cameraAlignerAdv.setErrors(errors);
  }

  clearErrorsCameraAligner() {
    this.controls.cameraAlignerAdv.clearErrors();
  }

  deleteRowCameraAligner(i) {
    this.controls.cameraAlignerAdv.removeByID(i);
  }

  setBasicAlignerState(state) {
    this.controls.cameraAlignerBasic.setState(state);
  }

  resetBasicAlignerCamera(values = [0, 0, 0]) {
    this.controls.cameraAlignerBasic.reset(values);
  }

  getBasicAlignerValue() {
    return this.controls.cameraAlignerBasic.cameraAngles;
  }

  flashObservations(id) {
    this.controls.cameraAlignerAdv.flashObservations(id);
  }

  setCurrentObservation(id, type) {
    this.controls.cameraAlignerAdv.setCurrentObservation(id, type);
  }

  /** Panoramic/Planar image functions */

  getCurrentCamera() {
    return this.cameraList.current;
  }

  getCurrentCameraName() {
    return this.cameraList.current?.name;
  }

  getCurrentCameraIndex() {
    return this.cameraList.cameras
      .indexOf(this.cameraList.current);
  }

  getNextCamera(index) {
    let numCameras = this.cameraList.cameras.length;
    return (index + 1) % numCameras;
  }

  getPreviousCamera(index) {
    let numCameras = this.cameraList.cameras.length;
    return (index + (numCameras - 1)) % numCameras;
  }

  updateCompass() {
    this.compass.addRotationCube();
    this.compass.addCompassLetters();
  }

  updateCompassOffset(offset) {
    this.compass.updateOffset(offset);
  }

  getCameraFeaturesForCSV = (assetID) => {
    return this.minimap.getByCSV(assetID);
  };

  getCamerasForCSV = (assetID) => {
    return this.cameraList.getByCSV(assetID);
  };

  getCameraRowsCSV = (cameras: CameraImage[]) => {
    return this.cameraList.getRowsCSV(cameras);
  };

  getAllCameras() {
    return this.cameraList.cameras;
  }

  getCameraFolders(cameras) {
    let folderPaths = [];
    let previousCSV = null;

    cameras.forEach(camera => {
      if (camera.csv === previousCSV) return;

      previousCSV = camera.csv;
      const imagePath = camera.path;
      folderPaths.push(path.dirname(imagePath));
    });

    return folderPaths;
  };

  getTagsForCSV = (assetID) => {
    return this.tags.getByID(assetID).tags;
  };

  getTagRowsCSV = (tags: TagItem[]) => {
    return this.tags.getRowsCSV(tags);
  };

  getModelStatus(assetID) {
    let file = this.cadHandler.getByID(assetID);

    return {
      assetPercent: file ? file.percent : 0.0,
      assetError: file ? file.error : false,
      assetIsLarge: false
    };
  }

  getCloudStatus(assetID) {
    let cloud = this.pointclouds.getByID(assetID);

    return {
      assetIsLarge: cloud instanceof GenericPointCloud ? cloud.isLargeFile : false,
      assetPercent: cloud instanceof GenericPointCloud ? cloud.percent : 0.0,
      assetError: cloud ? cloud.error : false
    };
  }

  setEmptyProject(state) {
    this.isEmpty = state;

    if (!this.isEmpty) return;
    this.setInitialZoomFlag(false);
    LocalScene.resetSceneShift();
  }

  reconcileAssets(assets, savedCameraState: SceneCameraState, savedTagID: string) {
    const {
      camerasFiles, pointClouds, orthoFiles,
      tagFiles, modelFiles, xmlFiles, webSources
    } = assets;

    // Tag CSV files
    this.tags.reconcile(tagFiles);

    // Model/Linework files
    this.cadHandler.reconcile(modelFiles);

    // Web sources
    this.webmapHandler.reconcile(webSources);

    // LandXML files
    this.measurements.reconcileXMLs(xmlFiles);

    // Orthomosaics
    this.orthoList.reconcile(orthoFiles);

    // Point Clouds
    const {numClouds, numCloudsRemoved} =
      this.pointclouds.reconcile(pointClouds);

    // Image CSV files
    const {numCameras, numCamerasAdded, numCamerasRemoved} =
      this.cameraList.reconcile(camerasFiles);

    // Try to apply forced tag perspective
    if (this.applySavedTagState(savedTagID, false)) return;

    // Try to apply forced scene state
    if (this.applySavedSceneState(savedCameraState, false)) return;

    const allCameras = this.cameraList.cameras;
    const numCamerasChanged = numCamerasAdded + numCamerasRemoved;

    // All pointclouds have been removed
    if ((numClouds === 0) && (numCloudsRemoved > 0) && (numCameras > 0)) {
      if (this.cameraList.cameraLoaded) {
        // Exit 3d mode if camera is loaded
        this.controls.setOrbitValues(false);
      } else {
        // Load first camera
        this.loadImage(allCameras[0].id, {
          centerOnImage: false
        });
      }
    }

    // All cameras have been removed, go back to orbit mode
    if ((numCameras === 0) && (numCamerasRemoved > 0)) {
      this.cameraList.setOrbitCamera();
      this.controls.resetSceneView();
      this.minimap.setActiveCamera();
      this.markers.clearArrowNavigation();
    }

    // Load first camera if nothing is loaded
    const loadCamera = (numCamerasChanged > 0)
      && !this.cameraList.cameraLoaded
      && !this.cameraList.newCameraLoading
      && (allCameras.length > 0);

    if (loadCamera) {
      this.loadImage(allCameras[0].id, {
        centerOnImage: false
      });
    }
  }

  preloadBookmarks(bookmarks: Bookmark[]) {
    this.cameraList.preloadBookmarks(bookmarks);
  }

  async jumpToTag(tagID: string) {
    const tag = this.tags.getTagByID(tagID);
    if (!tag) return;

    const mapPosition = tag.position.aerial;
    this.minimap.jumpToPosition(mapPosition);

    const position = tag.position.array;
    if (position.length === 2) {
      toast.success(t("toast.tag-jump-success"));
      return;
    }

    if (tag.sceneState) {
      // Apply tag scene state
      if (this.applySavedSceneState(tag.sceneState, true)) {
        toast.success(t("toast.tag-jump-success"));
        return;
      }
    }

    if (this.hasCameras) {
      // Tag has not scene state, so we use the closest camera instead
      const lookat = new DataProjectionCoordinate(position);
      const closestCameraID = this.cameraList
        .closestCameraToPosition(lookat);

      if (closestCameraID) {
        await this.loadImage(closestCameraID, {lookat});
        toast.success(t("toast.tag-jump-success"));
      } else {
        toast.warning(t("toast.camera-error-position"));
      }
    }
  }

  setCameraHeights(height) {
    this.cameraList.setCameraHeights(height);
  }

  setCameraAdjustments(adjustments) {
    this.cameraList.setCameraAdjustments(adjustments);
  }

  setCameraMatrix(matrix) {
    this.cameraList.setCameraMatrix(matrix);
    if (this.controls.cameraAlignerAdv.enabled) {
      this.controls.cameraAlignerAdv.redrawImageObsMarkers();
    }
  }

  resetCameraMatrix() {
    this.cameraList.resetCameraMatrix();
    if (this.controls.cameraAlignerAdv.enabled) {
      this.controls.cameraAlignerAdv.redrawImageObsMarkers();
    }
  }

  setInitialZoomFlag(state) {
    this.initialZoomApplied = state;
  }

  setInitialStateFlag(state) {
    this.initialStateApplied = state;
  }

  applySavedSceneState(state, resetInitialFlag: boolean) {
    if (resetInitialFlag) {
      this.setInitialStateFlag(false);
    }

    return state.orbit
      ? this.setOrbitState(state)
      : this.setCameraState(state);
  };

  applySavedTagState(tagID: string, resetInitialFlag: boolean) {
    const tag = this.tags.getTagByID(tagID);
    if (!tag) return false;

    if (resetInitialFlag) {
      this.setInitialStateFlag(false);
    }

    return this.setTagState(tagID);
  }

  applySavedAerialState(state) {
    this.minimap.map.updateSize();
    this.minimap.resetView(state);
  }

  resetDefaultAerialCamera() {
    this.minimap.setActiveCamera();
  }

  resetDefaultControlState() {
    this.setInitialStateFlag(false);
    this.controls.resetSceneView();
  }

  removeInteractions() {
    const measurements = this.controls.measurements;
    if (measurements.enabled) {
      measurements.measurementAerialView.removeInteraction();
    }
  }

  /** Private Methods */
  onKeyDown(event) {
    if (!this.focused) return;

    event.preventDefault();
    this.cameraList.onKeyDown(event);
    this.controls.onKeyDown(event);
  }

  onKeyUp(event) {
    this.controls.onKeyUp(event);
  }

  onMouseFocus(event) {
    if (event.type === 'mouseover') {
      this.focused = true;
    } else if (event.type === 'mouseout') {
      this.focused = false;
      this.clearCoordinates();
    }
  }

  initEvents() {
    this.onMouseFocus = this.onMouseFocus.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);

    // Keep track of our canvas focus
    this.containerElement.addEventListener('mouseover', this.onMouseFocus, false);
    this.containerElement.addEventListener('mouseout', this.onMouseFocus, false);

    // Keydown doesnt work on canvas
    window.addEventListener('keydown', this.onKeyDown, false);
    window.addEventListener('keyup', this.onKeyUp, false);
  }

  removeEvents() {
    this.containerElement.removeEventListener('mouseover', this.onMouseFocus, false);
    this.containerElement.removeEventListener('mouseout', this.onMouseFocus, false);
    window.removeEventListener('keydown', this.onKeyDown, false);
    window.removeEventListener('keyup', this.onKeyUp, false);
  }

  updateCoordinates(event) {
    if (!this.focused) return;

    this.updateMouseCoords(event);
  }

  clearCoordinates() {
    this.updateMouseCoords(null);
  }

  updateCamera() {
    // Update renderutils
    this.renderUtils.setCamera(this.camera);

    // Update GPU picker camera
    Object.values(this.gpuPickers).forEach(picker => {
      picker.setCamera(this.camera);
    });
  }

  updatePickers() {
    Object.keys(this.gpuPickers).forEach(name => {
      this.updatePicker(name);
    });
  }

  updatePicker(name) {
    this.gpuPickers[name].needsUpdate = true;
  }

  initRenderer() {
    this.camera = new PerspectiveCamera(90);
    this.camera.near = this.defaultNearPlane;
    this.camera.far = this.defaultFarPlane;
    this.camera.up = new Vector3(0, 0, 1);

    this.renderer = new WebGLRenderer({
      antialias: true,
      alpha: true,
      logarithmicDepthBuffer: false,
      powerPreference: "high-performance"
    });

    this.renderer.autoClear = false;
    this.renderer.setPixelRatio(this.pixelRatio);
    this.renderer.clear();

    this.renderUtils = new RenderUtils(
      this.renderer,
      this.camera,
      this.pixelRatio
    );
  }

  initGpuPickers() {
    this.gpuPickers = {
      markers: new GPUPicker(this, "markers"),
      default: new GPUPicker(this, "default"),
      tags: new GPUPicker(this, "tags")
    };

    Object.values(this.gpuPickers).forEach(picker => {
      this.renderUtils.addTarget(picker.target);
    });
  }

  initMaterials() {
    // Used for rendering panoramic as background
    this.imageRenderMaterial = new ImageMaterial();
    this.imageRenderTarget = new BasicRenderTarget(
      this.width, this.height, true);

    this.renderUtils.addTarget(this.imageRenderTarget);
  }

  initResize() {
    this.observer = onresize(this.containerElement, () => {
      this.handleWindowResize();
    });

    this.handleWindowResize();
  }

  updateSavedState() {
    setCameraState(this.sceneState);
  }

  handleWindowResize() {
    let aspect = this.width / this.height;

    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();

    this.renderer.domElement.style.display = "none";
    this.renderer.setSize(this.width, this.height);
    this.compass?.setSize(this.width, this.height);
    this.renderer.domElement.style.display = "block";

    this.updatePickers();

    // Re-render is required or else the div will flash white
    this.render();
  }

  updateMaterial(material, values) {
    let keys = Object.keys(values);
    keys.forEach(key => {
      material[key] = values[key];
    });
  }

  renderPanoramic() {
    // Render panoramic image
    this.renderUtils.setRenderTarget(this.imageRenderTarget, true);
    this.renderer.render(this.cameraList.scene, this.camera);

    // Update material texture with results
    this.updateMaterial(this.imageRenderMaterial, {
      colorMap: this.imageRenderTarget.texture
    });

    // Render to screen and clear depth
    this.renderUtils.renderToScreen(this.imageRenderMaterial);
    this.renderer.clearDepth();
  }

  renderSceneObjects() {
    // Render objects at full opacity
    this.renderUtils.renderScenes([
      this.markers.renderInFront ? null : this.markers.scene,
      this.cadHandler.scene
    ], 1.0);

    // Render pointcloud with transparency
    this.renderUtils.renderScenes([this.pointclouds.scene],
      this.pointclouds.transparency, false);

    // Render scene labels (mixed opacity)
    let sceneLabels = this.imageLabels.sceneLabels;
    this.renderUtils.renderScenes([sceneLabels.opacityScene],
      sceneLabels.opacity);
    this.renderUtils.renderScenes([sceneLabels.standardScene], 1.0);

    // Items here are rendered on top of everything, so they can always be seen
    this.renderUtils.renderScenes([
      this.markers.renderInFront ? this.markers.scene : null,
      ...this.controls.toolScenes,
      this.measurements.scene,
      this.tags.scene
    ], 1.0);
  }

  renderPickerScene() {
    this.renderUtils.setRenderTarget(null, true);

    Object.values(this.gpuPickers).forEach(picker => {
      // Force gpu picker data
      picker.needsUpdate = true;
      picker.update();

      // Render picker scene
      this.renderer.render(picker.scene, this.camera);
    });
  }

  updateSceneElements() {
    withTiming("point clouds", () => this.pointclouds.update());
    withTiming("measurements", () => this.measurements.update(this.changeDetector));
    withTiming("controls", () => this.controls.update(this.changeDetector));
    withTiming("camera list", () => this.cameraList.update());
    withTiming("minimap", () => this.minimap.update(this.changeDetector));
    withTiming("markers", () => this.markers.update());
    withTiming("image labels", () => this.imageLabels.update(this.changeDetector));
    withTiming("tags", () => this.tags.update());
    withTiming("cad handler", () => this.cadHandler.update());
  }

  updateAnnotations() {
    if (!this.enabled) return;
    if (this.annotations.length === 0) return;

    const pixelBuffer = 100;
    const minCameraDistance = 1.0;

    this.annotations.map(annotation => {
      const scenePos = new DataProjectionCoordinate(
        annotation.position).toScene();
      const distance = scenePos.distanceTo(
        this.camera.position);

      return {...annotation, position: scenePos, distance};
    }).sort((a, b) => {
      return b.distance - a.distance;
    }).forEach((annotation, zIndex) => {
      const {element, position, distance} = annotation;

      const uv = toScreenPosition(this, position, true);

      const hidden = (distance <= minCameraDistance)
        || (uv[0] < -pixelBuffer)
        || (uv[0] > this.width + pixelBuffer)
        || (uv[1] < -pixelBuffer)
        || (uv[1] > this.height + pixelBuffer)
        || this.isBehindCamera(position);

      if (hidden) {
        element.style.display = "none";
        return;
      }

      element.style.left = `${uv[0]}px`;
      element.style.top = `${uv[1]}px`;
      element.style['z-index'] = zIndex;
      element.style.display = "";
    });
  }

  render() {
    this.renderUtils.update();
    this.changeDetector.update();
    this.updateSceneElements();

    if (this.drawPickerScene) {
      this.renderPickerScene();
    } else {
      this.renderPanoramic();
      this.renderSceneObjects();
    }

    if (this.changeDetector.changed) {
      this.updateAnnotations();
      this.updateSavedState();
    }

    // Render compass element
    this.compass.render(this.showCompassElement,
      this.camera, this.orbitState);

    this.multiImageWindow.render();
  }

  animate() {
    if (!this.enabled) return;

    this.stats.begin();
    this.render();
    this.stats.end();

    requestAnimationFrame(() => {
      this.animate();
    });
  }
}

class StatsPanel {
  private stats: Stats;
  private viewer: Viewer;
  private nodesVisible;
  private pointsVisible;
  private enabled = isDevMode;
  private expanded = false;

  constructor(viewer: Viewer) {
    this.viewer = viewer;
    this.clear();
    this.init();
  }

  get indexToShow() {
    let childCount = this.stats.dom.children.length;
    return [0, childCount-1, childCount-2];
  }

  get parentElement() {
    return document.getElementById("debug-placeholder");
  }

  clear() {
    // Remove old stats panels
    [...document.getElementsByClassName("stats")].forEach(element => {
      element.remove();
    });
  }

  init() {
    if (!this.enabled) return false;

    this.stats = new Stats();
    this.stats.dom.classList.add("stats");
    this.setStyle();

    this.pointsVisible = this.stats.addPanel(
      new Stats.Panel('', '#ff8', '#221'));

    this.nodesVisible = this.stats.addPanel(
      new Stats.Panel('NODE', '#f8f', '#212'));

    [...this.stats.dom.children].forEach(element => {
      element.style.display = 'none';
      element.style.margin = '2px';
    });

    this.parentElement.appendChild(this.stats.dom);

    this.drawExpandedState();
    this.initEvents();
  }

  drawExpandedState() {
    // Hide all elements
    [...this.stats.dom.children].forEach(element => {
      element.style.display = 'none';
    });

    this.indexToShow.forEach(index => {
      this.stats.dom.children[index].style.display = 'inline-block';
    });

    if (this.expanded) {
      [...this.stats.dom.children].forEach(element => {
        element.style.display = 'inline-block';
      });
    }
  }

  initEvents() {
    this.stats.dom.onclick = (event) => {
      event.preventDefault();
      this.expanded = !this.expanded;
      this.drawExpandedState();
    };
  }

  setStyle() {
    this.stats.dom.style.position = "";
    this.stats.dom.style.top = "";
    this.stats.dom.style['z-index'] = "";
  }

  begin() {
    if (!this.enabled) return false;

    this.stats.begin();
  }

  end() {
    if (!this.enabled) return false;

    const {numSceneNodesVisible, numVisiblePoints,
      visiblePointsTarget} = this.viewer.pointclouds;

    this.pointsVisible.update(
      numVisiblePoints / 1000,
      visiblePointsTarget / 1000
    );

    this.nodesVisible.update(numSceneNodesVisible, 500);

    this.stats.end();
  }
}

/** @private */
const withTiming = (name, callback, timeout=10) => {
  const timeStart = performance.now();

  callback();

  const timeElapsed = performance.now() - timeStart;
  if (timeElapsed < timeout) return;

  console.warn(`Slow update detected: ${name} took ${timeElapsed.toFixed(2)}ms`);
};